import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Field } from '@/modules/home';
import AddFieldCTA from '@/modules/home/components/PageContent/Operations/Fields/ExpandableFieldList/AddFieldCTA';
import {
  filteredFieldsByArchived,
  sortFieldsByStatus,
} from '@/modules/home/components/PageContent/Operations/Fields/logic';
import { useHomePageContext } from '@/modules/home/context';
import { IHome } from '@/modules/home/types';
import { AddPropertyField } from '@/modules/settings/property';
import { Button, Typography } from '@/shared/components/atoms';
import { Collapse } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import { EntityType } from '@/shared/constant/enums';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import Location from '~/images/shared/Location.svg';

const MAX_ITEMS = 22;

const ExpandableFieldList: React.FC<{ fields: IHome.IField[] | undefined }> = ({
  fields,
}) => {
  const { t } = useTranslation();

  const [store] = useStore((store) => store);
  const selectedAsset = store?.asset;
  const isDemoFarm = store?.isDemoFarm;

  const {
    state: { date },
  } = useHomePageContext();

  const [isExpanded, setIsExpanded] = useState(false);

  const sortedFields = fields ? sortFieldsByStatus(fields) : [];
  const filteredFields = filteredFieldsByArchived(sortedFields, date);

  const { openModal } = useModal();

  const handleAddField = () => {
    isDemoFarm
      ? openModal(<DemoCTA />)
      : openModal(<AddPropertyField selectedAsset={selectedAsset} />, {
          title: <Typography variant='h2'>{t('fields-and-crops')}</Typography>,
          variant: 'full',
        });
  };

  useEffect(() => {
    setIsExpanded(false);
  }, [date]);

  return fields?.length && filteredFields?.length ? (
    <>
      {!isExpanded && (
        <div className='mt-24 mb-16 flex flex-wrap gap-12'>
          {filteredFields.slice(0, MAX_ITEMS).map((field) => (
            <Field key={field.id} field={field} />
          ))}
          <AddFieldCTA handleAddField={handleAddField} />
        </div>
      )}
      {filteredFields.length > MAX_ITEMS && (
        <Collapse
          header={t(isExpanded ? 'Show Less' : 'Show All')}
          itemClassName='justify-center gap-8 order-2 py-16'
          containerPadding='p-0'
          childrenPadding='p-0'
          handleShowCollapseContent={(state) => setIsExpanded(state)}
        >
          <div className='mt-24 mb-16 flex flex-wrap gap-12'>
            {filteredFields.map((field) => (
              <Field key={field.id} field={field} />
            ))}
            <AddFieldCTA handleAddField={handleAddField} />
          </div>
        </Collapse>
      )}
    </>
  ) : (
    <div className='flex w-full flex-col items-center justify-center gap-16 py-30'>
      <span className='text-primary-60'>
        <Location width='48' height='48' />
      </span>
      <div className='text-center'>
        <Typography variant='subtitle' weight='bold'>
          {t(
            selectedAsset?.entityTypeId === EntityType.FARM
              ? 'No Fields'
              : 'No Lines'
          )}
        </Typography>
        <Button
          size='none'
          onClick={handleAddField}
          variant='text'
          className='mt-16 flex h-40 items-center justify-center border-xs border-outline-default px-16'
        >
          {t('add-new-field')}
        </Button>
      </div>
    </div>
  );
};

export { ExpandableFieldList };
